import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import NavItemLabel from './NavItemLabel';

/**
 * SubMenuTrigger — a nav item button that opens a sub-menu panel (drilldown mode).
 *
 * @param {Object} props
 * @param {NavigationItem} props.item - Navigation item data
 * @param {(item: NavigationItem, triggerRef?: React.RefObject) => void} props.onSubmenuOpen - Called with the item when triggered
 * @param {boolean} props.isActive - Whether this item or a descendant is active
 * @param {boolean} [props.isLeafActive] - Whether a direct child page is the current URL
 */
const SubMenuTrigger = ({ item, onSubmenuOpen, isActive, isLeafActive }) => {
	const triggerButtonRef = useRef(null);
	const { activeDrillDownItem } = useDrawerNavigation();
	const activeClass = isActive ? 'ws-nav-drawer__nav-item--active' : '';
	const leafActiveClass = isLeafActive
		? 'ws-nav-drawer__nav-item--leaf-active'
		: '';

	const isThisItemOpen =
		activeDrillDownItem !== null &&
		(item.id !== undefined
			? item.id === activeDrillDownItem.id
			: item.label === activeDrillDownItem.label);

	const handleClick = () => {
		onSubmenuOpen(item, triggerButtonRef);
	};

	return (
		<button
			type="button"
			ref={triggerButtonRef}
			aria-expanded={isThisItemOpen}
			aria-controls="ws-nav-drawer-drilldown-panel"
			aria-haspopup="dialog"
			className={setClassNames([
				'ws-nav-drawer__nav-item',
				'ws-nav-drawer__nav-item--drilldown',
				'py-4',
				'px-0',
				'd-flex',
				'align-items-center',
				'justify-content-between',
				'w-100',
				'bg-transparent',
				'text-left',
				activeClass,
				leafActiveClass
			])}
			onClick={handleClick}
		>
			<NavItemLabel label={item.label} className="h4" />
			<i
				className={setClassNames([
					'ddc-icon',
					'ddc-icon-skinny-arrow-right',
					'ws-nav-drawer__nav-item-icon',
					'ddc-font-size-large',
					'd-flex',
					'flex-shrink-0',
					'align-items-center',
					'justify-content-center'
				])}
				aria-hidden="true"
			/>
		</button>
	);
};

SubMenuTrigger.propTypes = {
	item: PropTypes.shape({
		label: PropTypes.string.isRequired,
		url: PropTypes.string,
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		selected: PropTypes.bool,
		matched: PropTypes.bool
	}).isRequired,
	onSubmenuOpen: PropTypes.func.isRequired,
	isActive: PropTypes.bool,
	isLeafActive: PropTypes.bool
};

export default SubMenuTrigger;
